import { useRef, useState } from 'react';
import { useEnabledTicketingIntegration } from './integrations';

type Options = {
  envId: string | undefined;
  // When provided, the ticket id is owned externally (e.g. by react-hook-form).
  // The hook becomes a thin wrapper that only manages integration selection
  // and the create-ticket dialog state, and proxies ticket-id reads/writes
  // through these props.
  ticketId?: string | null;
  setTicketId?: (id: string | null) => void;
};

const useTicketField = ({
  envId,
  ticketId: externalTicketId,
  setTicketId: externalSetTicketId,
}: Options) => {
  const isControlled = externalSetTicketId !== undefined;

  const [localTicketId, setLocalTicketId] = useState<string | null>(null);
  const ticketId = isControlled ? externalTicketId ?? null : localTicketId;
  const setTicketId = isControlled ? externalSetTicketId! : setLocalTicketId;

  const ticketIdRef = useRef<string | null>(null);
  ticketIdRef.current = ticketId;

  const [selectedIntegrationId, setSelectedIntegrationId] = useState<
    string | undefined
  >(undefined);
  const [createTicketDialogOpen, setCreateTicketDialogOpen] = useState(false);

  const { requireTicketId, enabledIntegrations } =
    useEnabledTicketingIntegration({ envId });

  const activeIntegration =
    enabledIntegrations.find((i) => i.id === selectedIntegrationId) ??
    enabledIntegrations[0];

  const handleIntegrationSelect = (id: string) => {
    setSelectedIntegrationId(id);
    setTicketId(null);
  };

  const handleCreateTicketClick = (integrationId?: string) => {
    if (integrationId) setSelectedIntegrationId(integrationId);
    setCreateTicketDialogOpen(true);
  };
  const handleCreateTicketClose = () => setCreateTicketDialogOpen(false);

  const handleTicketCreated = (key: string) => {
    setTicketId(key);
    setCreateTicketDialogOpen(false);
  };

  const resetTicket = () => {
    setTicketId(null);
    setSelectedIntegrationId(undefined);
    setCreateTicketDialogOpen(false);
  };

  return {
    ticketId,
    setTicketId,
    ticketIdRef,
    requireTicketId,
    enabledIntegrations,
    activeIntegration,
    handleIntegrationSelect,
    createTicketDialogOpen,
    handleCreateTicketClick,
    handleCreateTicketClose,
    handleTicketCreated,
    resetTicket,
  };
};

export default useTicketField;
